import { useState, useEffect, useRef } from 'react';
import { meetings } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Upload, Mic, FileAudio, Loader2, Copy, Check, Trash2, Languages, Clock,
  Sparkles, ListTodo, CheckCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const SUPPORTED_LANGUAGES = [
  { code: 'auto', label: 'Auto detectar' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'ht', label: 'Kreyòl Ayisyen (Creol Haitiano)' },
];

function UploadZone({ onFileSelect, isUploading }) {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) onFileSelect(file);
  };

  return (
    <div
      onDrop={handleDrop}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onClick={() => fileInputRef.current?.click()}
      className={`
        border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
        ${dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}
        ${isUploading ? 'pointer-events-none opacity-50' : ''}
      `}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".mp3,.mp4,.mpeg,.mpga,.m4a,.wav,.webm"
        onChange={(e) => e.target.files[0] && onFileSelect(e.target.files[0])}
        className="hidden"
        data-testid="audio-file-input"
      />
      
      <div className="flex flex-col items-center gap-3">
        <div className="p-4 bg-primary/10 rounded-full">
          <Upload className="h-8 w-8 text-primary" />
        </div>
        <div>
          <p className="font-medium">Arrastra un archivo de audio aquí</p>
          <p className="text-sm text-muted-foreground">o haz clic para seleccionar</p>
        </div>
        <div className="flex flex-wrap gap-2 justify-center mt-2">
          {['mp3', 'mp4', 'wav', 'm4a', 'webm'].map((ext) => (
            <Badge key={ext} variant="outline" className="text-xs">.{ext}</Badge>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">Máximo 25MB</p>
      </div>
    </div>
  );
}

function ExtractTasksDialog({ open, onClose, meeting, onTasksCreated }) {
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [creating, setCreating] = useState(false);

  const handleExtract = async () => {
    setExtracting(true);
    try {
      const res = await meetings.extractTasks(meeting.meeting_id);
      setExtracted(res.data);
      setSelectedTasks(res.data.tasks.map((_, i) => i));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al extraer tareas');
    } finally {
      setExtracting(false);
    }
  };

  const handleCreateTasks = async () => {
    if (selectedTasks.length === 0) {
      toast.error('Selecciona al menos una tarea');
      return;
    }

    setCreating(true);
    try {
      const tasksToCreate = selectedTasks.map(i => extracted.tasks[i]);
      const res = await meetings.createTasks(meeting.meeting_id, tasksToCreate);
      toast.success(`${res.data.created_count} tarea(s) creada(s)`);
      onTasksCreated?.();
      onClose();
    } catch (err) {
      toast.error('Error al crear tareas');
    } finally {
      setCreating(false);
    }
  };

  const toggleTask = (index) => {
    setSelectedTasks(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const priorityColors = {
    high: 'bg-rose-100 text-rose-700 border-rose-300',
    medium: 'bg-amber-100 text-amber-700 border-amber-300',
    low: 'bg-slate-100 text-slate-700 border-slate-300',
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-violet-500" />
            Extraer Tareas con IA
          </DialogTitle>
        </DialogHeader>

        {!extracted ? (
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              La IA analizará la transcripción de la reunión para identificar action items y tareas pendientes.
            </p>
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm font-medium mb-1">{meeting?.title || 'Reunión'}</p>
              <p className="text-xs text-muted-foreground">
                {meeting?.transcription?.slice(0, 200)}...
              </p>
            </div>
            <Button 
              onClick={handleExtract} 
              disabled={extracting}
              className="w-full gap-2"
            >
              {extracting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Analizando con IA...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Analizar Transcripción
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {/* Summary */}
            {extracted.summary && (
              <div className="bg-violet-50 p-4 rounded-lg border border-violet-200">
                <p className="text-sm font-medium text-violet-800 mb-1">Resumen de la reunión:</p>
                <p className="text-sm text-violet-700">{extracted.summary}</p>
              </div>
            )}

            {/* Tasks List */}
            <div className="space-y-2">
              <p className="text-sm font-medium">
                Tareas identificadas ({extracted.tasks.length}):
              </p>
              
              {extracted.tasks.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No se identificaron tareas en esta transcripción</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {extracted.tasks.map((task, index) => (
                    <div 
                      key={index}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedTasks.includes(index) 
                          ? 'bg-primary/5 border-primary' 
                          : 'hover:bg-muted/50'
                      }`}
                      onClick={() => toggleTask(index)}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox 
                          checked={selectedTasks.includes(index)}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium">{task.title}</p>
                            <Badge className={`text-xs ${priorityColors[task.priority]}`}>
                              {task.priority === 'high' ? 'Alta' : task.priority === 'medium' ? 'Media' : 'Baja'}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{task.description}</p>
                          {task.assigned_name && (
                            <p className="text-xs text-primary mt-1">→ {task.assigned_name}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            {extracted.tasks.length > 0 && (
              <div className="flex items-center justify-between pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  {selectedTasks.length} de {extracted.tasks.length} seleccionadas
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={onClose}>
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleCreateTasks}
                    disabled={creating || selectedTasks.length === 0}
                    className="gap-2"
                  >
                    {creating ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ListTodo className="h-4 w-4" />
                    )}
                    Crear Tareas
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TranscriptionCard({ transcription, onDelete, onCopy, onExtractTasks }) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(transcription.transcription);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    onCopy?.();
  };

  const text = transcription.transcription || '';
  const previewText = text.slice(0, 200);
  const hasMore = text.length > 200;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <FileAudio className="h-5 w-5 text-primary" />
            <div>
              <CardTitle className="text-base">{transcription.title || transcription.filename || 'Audio'}</CardTitle>
              <p className="text-xs text-muted-foreground">
                {formatDateTime(transcription.created_at)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => onExtractTasks(transcription)} 
              className="gap-1 text-violet-600 border-violet-300 hover:bg-violet-50"
            >
              <Sparkles className="h-3 w-3" />
              Extraer Tareas
            </Button>
            <Button size="icon" variant="ghost" onClick={handleCopy} className="h-8 w-8">
              {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
            </Button>
            <Button size="icon" variant="ghost" onClick={() => onDelete(transcription.meeting_id)} className="h-8 w-8 text-destructive">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2 mb-3">
          {transcription.language && (
            <Badge variant="outline" className="gap-1">
              <Languages className="h-3 w-3" />
              {SUPPORTED_LANGUAGES.find(l => l.code === transcription.language)?.label || transcription.language}
            </Badge>
          )}
          {transcription.tasks_extracted && (
            <Badge className="gap-1 bg-violet-100 text-violet-700">
              <CheckCircle className="h-3 w-3" />
              Tareas extraídas
            </Badge>
          )}
        </div>
        
        <div className="bg-muted/50 rounded-md p-3">
          <p className="text-sm whitespace-pre-wrap">
            {expanded ? text : previewText}
            {hasMore && !expanded && '...'}
          </p>
          {hasMore && (
            <Button 
              variant="link" 
              size="sm" 
              onClick={() => setExpanded(!expanded)}
              className="p-0 h-auto mt-2"
            >
              {expanded ? 'Ver menos' : 'Ver más'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function Meetings() {
  const [transcriptionsList, setTranscriptionsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [language, setLanguage] = useState('auto');
  const [extractDialogOpen, setExtractDialogOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);

  useEffect(() => {
    loadTranscriptions();
  }, []);

  const loadTranscriptions = async () => {
    try {
      const res = await meetings.getAll();
      setTranscriptionsList(res.data || []);
    } catch (err) {
      console.error('Error loading transcriptions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (file) => {
    if (file.size > 25 * 1024 * 1024) {
      toast.error('El archivo es muy grande. Máximo 25MB.');
      return;
    }
    setSelectedFile(file);
  };

  const handleTranscribe = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadProgress(10);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 2000);

      const formData = new FormData();
      formData.append('file', selectedFile);
      if (language && language !== 'auto') {
        formData.append('language', language);
      }

      await meetings.upload(formData);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      toast.success('Transcripción completada');
      setSelectedFile(null);
      loadTranscriptions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al transcribir');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id) => {
    try {
      await meetings.delete(id);
      toast.success('Transcripción eliminada');
      loadTranscriptions();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const handleCopy = () => {
    toast.success('Texto copiado al portapapeles');
  };

  const handleExtractTasks = (meeting) => {
    setSelectedMeeting(meeting);
    setExtractDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="meetings-page">
      <div>
        <h1 className="text-3xl font-bold font-heading tracking-tight">Reuniones</h1>
        <p className="text-muted-foreground">Transcripción de audio con IA y extracción de tareas</p>
      </div>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg uppercase tracking-wide flex items-center gap-2">
            <Mic className="h-5 w-5" />
            Nueva Transcripción
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <UploadZone onFileSelect={handleFileSelect} isUploading={uploading} />
          
          {selectedFile && (
            <div className="bg-muted/50 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileAudio className="h-8 w-8 text-primary" />
                  <div>
                    <p className="font-medium">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedFile(null)}
                  disabled={uploading}
                >
                  Cancelar
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Idioma del audio</Label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger data-testid="language-select">
                      <SelectValue placeholder="Auto detectar" />
                    </SelectTrigger>
                    <SelectContent>
                      {SUPPORTED_LANGUAGES.map((lang) => (
                        <SelectItem key={lang.code} value={lang.code}>
                          {lang.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {uploading && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Transcribiendo...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <Progress value={uploadProgress} />
                </div>
              )}
              
              <Button 
                onClick={handleTranscribe} 
                disabled={uploading}
                className="w-full"
                data-testid="transcribe-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <Mic className="h-4 w-4 mr-2" />
                    Transcribir Audio
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transcriptions List */}
      <div>
        <h2 className="font-heading text-lg uppercase tracking-wide mb-4">
          Transcripciones ({transcriptionsList.length})
        </h2>
        
        {transcriptionsList.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <FileAudio className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hay transcripciones aún</p>
              <p className="text-sm">Sube un archivo de audio para comenzar</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {transcriptionsList.map((trans) => (
              <TranscriptionCard
                key={trans.meeting_id}
                transcription={trans}
                onDelete={handleDelete}
                onCopy={handleCopy}
                onExtractTasks={handleExtractTasks}
              />
            ))}
          </div>
        )}
      </div>

      {/* Extract Tasks Dialog */}
      <ExtractTasksDialog
        open={extractDialogOpen}
        onClose={() => {
          setExtractDialogOpen(false);
          setSelectedMeeting(null);
        }}
        meeting={selectedMeeting}
        onTasksCreated={loadTranscriptions}
      />
    </div>
  );
}
